import { basePaths, pages } from '~/app/routes'
import {
    OperationalPlannerFilter,
    operationalPlannerFilters,
    operationalPlannerMonthFilters,
    selectAvailableByCountValue as selectOpAvailableByCountValue,
    selectAvailableDurationMaxFilter as selectOpAvailableDurationMaxFilter,
    selectAvailableDurationMinFilter as selectOpAvailableDurationMinFilter,
    selectLocationValues as selectOpLocationValues,
    selectOperationalPlannerFiltersForView as selectOperationalPlannerFilters,
    selectPractitionerValues as selectOpPractitionerValues,
    selectSurgeryTypeGroupValues as selectOpSurgeryTypeGroupValues,
    selectWeekdayValues as selectOpWeekdayValues,
} from '~/store/slices/operationalPlannerFilterSlice'
import {
    selectLocationValues as selectSurgeonLocationValues,
    selectPractitionerValues as selectSurgeonPractitionerValues,
    selectStatusValues as selectSurgeonStatusValues,
    selectSurgeonSchedulerFiltersForView,
    SurgeonSchedulerFilter,
    surgeonSchedulerLocationFilters,
    surgeonSchedulerPractitionerFilters,
} from '~/store/slices/surgeonSchedulerFilterSlice'
import {
    selectLocationValues as selectSsLocationValues,
    selectSurgerySchedulerFilters,
    SurgerySchedulerFilter,
    surgerySchedulerFilters,
} from '~/store/slices/surgerySchedulerFilterSlice'
import { FilterSliceKey, PageFilterType } from '~/store/slices/types'
import {
    selectASAValues as selectWlASAValues,
    selectCancerValues as selectWlCancerValues,
    selectClearedValues as selectWlClearedValues,
    selectConfirmedValues as selectWlConfirmedValues,
    selectDayOvernightValues as selectWlDayOvernightValues,
    selectDiagnosisGroupValues as selectWlDiagnosisGroupValues,
    selectPatientAvailabilityFromFilter as selectWlPatientAvailabilityFromFilter,
    selectPatientAvailabilityToFilter as selectWlPatientAvailabilityToFilter,
    selectPractitionerComboValues as selectWlPractitionerComboValues,
    selectPrioritizationValues as selectWlPrioritizationValues,
    selectShortNoticeValues as selectWlShortNoticeValues,
    selectSurgeryCategoryValues as selectWlSurgeryCategoryValues,
    selectSurgeryLetterStatus as selectWlSurgeryLetterStatus,
    selectWaitingListFiltersForView as selectWaitingListFilters,
    selectWaitingValues as selectWlWaitingValues,
    waitingListAllAndScheduledFilters,
    WaitingListFilter,
    waitingListUnscheduledFilters,
} from '~/store/slices/waitingListFilterSlice'
import type { StoreState } from '~/store/store'

/**
 * The pages that have filters and saved filter profiles (view option profiles), and what each page's filters are.
 * Code that works with the current page's filters looks the page up here by its path, so a page with filters is
 * added in this one place.
 *
 * - `basePath`: the routes under it belong to the page.
 * - `sliceKey`: the store slice that holds the page's filters.
 * - `filterTypes`: every filter type the page's profiles save. A profile is saved with all of them, `[]` for the
 *   ones that are off, because the API merges an update into the stored profile. Pages with tabs list the filter
 *   types of every tab.
 * - `selectors`: the slice's selectors, for code that reads the filters without knowing the page.
 * - `appliedFilterTypesByView`: on a page with views, the filter types each view's data reads, by view path. The
 *   views share one slice, so a filter set in one view stays on in the others; where a view doesn't read it, the
 *   filter bar shows it muted rather than hiding it. A filter applies when it changes what the view shows, by
 *   narrowing it or by highlighting what matches. See DR-010.
 */
export const pageFilters = {
    'or-schedule': {
        basePath: basePaths.OR_SCHEDULE,
        sliceKey: 'operationalPlannerFilters',
        filterTypes: [...new Set([...operationalPlannerFilters, ...operationalPlannerMonthFilters])],
        // `block-availability` has no control and nothing reads it. Weekdays narrow only a range of days.
        appliedFilterTypesByView: {
            [pages.OPERATIONAL_PLANNER.views.day.path]: operationalPlannerFilters.filter(type => type !== 'block-availability'),
            [pages.OPERATIONAL_PLANNER.views.calendar.path]: operationalPlannerFilters.filter(type => type !== 'block-availability'),
            [pages.OPERATIONAL_PLANNER.views.month.path]: operationalPlannerMonthFilters.filter(type => type !== 'block-availability'),
        },
        selectors: {
            selectAllFilters: selectOperationalPlannerFilters,
            selectAvailableDurationMinFilter: selectOpAvailableDurationMinFilter,
            selectAvailableDurationMaxFilter: selectOpAvailableDurationMaxFilter,
            selectAvailableByCountValue: selectOpAvailableByCountValue,
            selectPractitionerValues: selectOpPractitionerValues,
            selectSurgeryTypeGroupValues: selectOpSurgeryTypeGroupValues,
            selectWeekdayValues: selectOpWeekdayValues,
            selectLocationValues: selectOpLocationValues,
        },
    },
    patients: {
        basePath: basePaths.PATIENTS,
        sliceKey: 'waitingListFilters',
        filterTypes: [...new Set([...waitingListAllAndScheduledFilters, ...waitingListUnscheduledFilters])],
        // Every view filters the same list with every filter, whichever ones its pane offers. Nothing reads
        // `letter-status`: "Brev ikke sendt" is a `follow-up` value.
        appliedFilterTypesByView: Object.fromEntries(
            Object.values(pages.WAITING_LIST.views).map(view => [view.path, waitingListAllAndScheduledFilters.filter(type => type !== 'letter-status')])
        ),
        selectors: {
            selectAllFilters: selectWaitingListFilters,
            selectASAValues: selectWlASAValues,
            selectClearedValues: selectWlClearedValues,
            selectConfirmedValues: selectWlConfirmedValues,
            selectDayOvernightValues: selectWlDayOvernightValues,
            selectDiagnosisGroupValues: selectWlDiagnosisGroupValues,
            selectCancerValues: selectWlCancerValues,
            selectPatientAvailabilityFilterFrom: selectWlPatientAvailabilityFromFilter,
            selectPatientAvailabilityFilterTo: selectWlPatientAvailabilityToFilter,
            selectPractitionerComboValues: selectWlPractitionerComboValues,
            selectPrioritizationValues: selectWlPrioritizationValues,
            selectShortNoticeValues: selectWlShortNoticeValues,
            selectSurgeryCategoryValues: selectWlSurgeryCategoryValues,
            selectSurgeryLetterStatus: selectWlSurgeryLetterStatus,
            selectWaitingValues: selectWlWaitingValues,
        },
    },
    'surgery-scheduler': {
        basePath: basePaths.SURGERY_SCHEDULER,
        sliceKey: 'surgerySchedulerFilters',
        filterTypes: surgerySchedulerFilters,
        selectors: {
            selectAllFilters: selectSurgerySchedulerFilters,
            selectLocationValues: selectSsLocationValues,
        },
    },
    'surgeon-scheduler': {
        basePath: basePaths.SURGEON_SCHEDULER,
        sliceKey: 'surgeonSchedulerFilters',
        filterTypes: [...new Set([...surgeonSchedulerLocationFilters, ...surgeonSchedulerPractitionerFilters])],
        // Statuses grey out other activities and surgeons in Stuer dim the rest; both count as applied. Fagområde
        // has no filters or filter profiles.
        appliedFilterTypesByView: {
            [pages.SURGEON_SCHEDULER.views.practitioners.path]: surgeonSchedulerPractitionerFilters,
            [pages.SURGEON_SCHEDULER.views.locations.path]: surgeonSchedulerLocationFilters,
            [pages.SURGEON_SCHEDULER.views.allocations.path]: [],
        },
        selectors: {
            selectAllFilters: selectSurgeonSchedulerFiltersForView,
            selectPractitionerValues: selectSurgeonPractitionerValues,
            selectLocationValues: selectSurgeonLocationValues,
            selectStatusValues: selectSurgeonStatusValues,
        },
    },
} satisfies Record<string, PageFilterConfig>

/** A page that has filters and saved filter profiles, as the API names it in `page_type`. */
export type BaseRoutesWithFilters = keyof typeof pageFilters

export type AnyFilter = OperationalPlannerFilter | WaitingListFilter | SurgerySchedulerFilter | SurgeonSchedulerFilter

export type FilterSelectors = {
    selectAllFilters: (state: StoreState) => AnyFilter[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: (state: StoreState) => any
}

type PageFilterConfig = {
    basePath: string
    sliceKey: FilterSliceKey
    filterTypes: readonly PageFilterType[]
    appliedFilterTypesByView?: Record<string, readonly PageFilterType[]>
    selectors: FilterSelectors
}

/** The page with filters that a path belongs to, if any. */
export const findPageTypeByPathname = (pathname: string) =>
    (Object.keys(pageFilters) as BaseRoutesWithFilters[]).find(pageType => pathname.startsWith(pageFilters[pageType].basePath))

/** The page with filters that a path belongs to. Throws for a path outside them. */
export const getPageTypeByPathname = (pathname: string): BaseRoutesWithFilters => {
    const pageType = findPageTypeByPathname(pathname)
    if (!pageType) throw new Error(`No route found for pathname: ${pathname}`)
    return pageType
}

/**
 * The filter types the view at `pathname` applies, or `undefined` when every filter applies: on a page without
 * views, and on a view the page doesn't list, where muting filters that do narrow it would mislead more than
 * showing them plain. See `appliedFilterTypesByView`.
 */
export const appliedFilterTypesForView = (pathname: string): readonly PageFilterType[] | undefined => {
    const pageType = findPageTypeByPathname(pathname)
    const page: PageFilterConfig | undefined = pageType ? pageFilters[pageType] : undefined
    const byView = page?.appliedFilterTypesByView
    if (!byView) return undefined

    return Object.entries(byView).find(([viewPath]) => pathname.startsWith(viewPath))?.[1]
}
